import { create } from "zustand";
import { Robot } from "@/types/Robot";
import { persist } from "zustand/middleware";
import { Peerbot } from "@/types/Robots/Peerbot";
import { Misty } from "@/types/Robots/Misty";
import { Furhat } from "@/types/Robots/Furhat";
import { Color, connectionMessage, Emotion, Message } from "@/types/Message";
import { DebugRobot } from "@/types/Robots/DebugRobot";
import { LocalRobot } from "@/types/Robots/LocalRobot";

interface ConnectedRobotsStore {
  connectedRobots: Robot[];
  disconnectAccountBasedRobots: () => void;
  connect: (robot: Robot) => void;
  disconnectByIndex: (ind: number) => void;
  disconnectById: (id: string) => void;
  setVoiceOfRobotId: (voiceUri: string, id: string) => void;
  toggleRobotId: (id: string) => void;
  spotlightRobotId: (id: string) => void;
  sendEmotion: (emotion: Emotion) => void;
  sendColor: (color: Color) => void;
  sendSpeech: (speech: string) => void;
  sendExpression: (expression: Message) => void;
  updateRobot: (updatedRobot: Robot) => void;
}

function isRobotInList(robot: Robot, listOfRobots: Robot[]) {
  return listOfRobots.some((listRobot) => {
    if (robot.kind != listRobot.kind) {
      return false;
    }
    switch (listRobot.kind) {
      case "Peerbot":
        return (
          listRobot instanceof Peerbot &&
          robot instanceof Peerbot &&
          listRobot.connection == robot.connection
        );
      case "Misty":
        return (
          listRobot instanceof Misty &&
          robot instanceof Misty &&
          listRobot.ip == robot.ip
        );
      case "Furhat":
        return (
          listRobot instanceof Furhat &&
          robot instanceof Furhat &&
          listRobot.ip == robot.ip
        );
      default:
        return false;
    }
  });
}

export const useConnectedRobotsStore = create<ConnectedRobotsStore>()(
  persist(
    (set, get) => ({
      connectedRobots: [],
      disconnectAccountBasedRobots: () => {
        set((state) => ({
          connectedRobots: state.connectedRobots.filter((rob) => {
            if (rob.kind === "Peerbot") {
              return !(rob as Peerbot).isIPBased;
            }
            return true;
          }),
        }));
      },
      connect: (robot) => {
        if (!isRobotInList(robot, get().connectedRobots)) {
          set((state) => ({
            connectedRobots: [...state.connectedRobots, robot],
          }));
        }
        robot.sendMessage(connectionMessage);
      },
      disconnectByIndex: (ind) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.filter((rob, i) => {
            if (i === ind) {
              try {
                rob.disconnect?.();
              } catch (e) {
                console.warn(
                  "ConnectedRobotsStore: Error disconnecting robot:",
                  e,
                );
              }
              return false;
            }
            return true;
          }),
        }));
      },
      disconnectById: (id) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.filter((rob) => {
            if (rob.id === id) {
              try {
                rob.disconnect?.();
              } catch (e) {
                console.warn(
                  "ConnectedRobotsStore: Error disconnecting robot:",
                  e,
                );
              }
              return false;
            }
            return true;
          }),
        }));
      },
      setVoiceOfRobotId: (voiceUri, id) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.map((rob) => {
            if (rob.id === id && rob.setVoice) {
              rob.setVoice(voiceUri);
            }
            return rob;
          }),
        }));
      },
      toggleRobotId: (id) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.map((rob) => {
            if (rob.id === id) {
              rob.isOn = !rob.isOn;
            }
            return rob;
          }),
        }));
      },
      spotlightRobotId: (id) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.map((rob) => {
            if (rob.id === id) {
              rob.isOn = true;
            } else {
              rob.isOn = false;
            }
            return rob;
          }),
        }));
      },
      sendColor: (color) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.map((rob) => {
            if (rob.isOn) {
              rob.sendColor(color);
            }
            return rob;
          }),
        }));
      },
      sendEmotion: (emotion) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.map((rob) => {
            if (rob.isOn) {
              rob.sendEmotion(emotion);
            }
            return rob;
          }),
        }));
      },
      sendSpeech: (speech) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.map((rob) => {
            if (rob.isOn) {
              rob.sendSpeech(speech);
            }
            return rob;
          }),
        }));
      },
      sendExpression: (expression) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.map((rob) => {
            if (rob.isOn) {
              rob.sendMessage(expression);
            }
            return rob;
          }),
        }));
      },
      updateRobot: (updatedRobot) => {
        set((state) => ({
          connectedRobots: state.connectedRobots.map((rob) => {
            if (rob.id === updatedRobot.id) {
              return updatedRobot;
            }
            return rob;
          }),
        }));
      },
    }),
    {
      name: "connectedRobots",
      version: 1,
      merge: (persistedState, currentState) => {
        const persistedConnectedRobotsStore =
          persistedState as ConnectedRobotsStore;
        const parsedConnectedRobots =
          persistedConnectedRobotsStore.connectedRobots
            .map((persistedRob: Robot) => {
              switch (persistedRob.kind) {
                case "DebugRobot": {
                  const persistedDebug = persistedRob as DebugRobot;
                  return new DebugRobot(persistedDebug.messageLog);
                }
                case "LocalRobot": {
                  const persistedLocal = persistedRob as LocalRobot;
                  const localBot = new LocalRobot();
                  localBot.setVoice(persistedLocal.selectedVoice.voiceURI);
                  return localBot;
                }
                case "Peerbot": {
                  const persistedPeerbot = persistedRob as Peerbot;
                  return new Peerbot(
                    persistedPeerbot.connection,
                    persistedPeerbot.caller,
                  );
                }
                case "Misty": {
                  const persistedMisty = persistedRob as Misty;
                  return new Misty(
                    persistedMisty.ip,
                    persistedMisty.useMistyTTS,
                  );
                }
                case "Furhat": {
                  const persistedFurhat = persistedRob as Furhat;
                  return new Furhat(
                    persistedFurhat.ip,
                    persistedFurhat.useFurhatTTS,
                  );
                }
                default:
                  return null;
              }
            })
            .filter((r) => r != null) as Robot[];
        const mergedState = {
          ...currentState,
          connectedRobots: parsedConnectedRobots,
        };
        return mergedState;
      },
    },
  ),
);
